<template>
  <div class="patients-page">
    <v-breadcrumbs :items="items" class="px-0 pb-2"></v-breadcrumbs>

    <!-- Header -->
    <v-card class="patients-header mb-4" elevation="1">
      <v-card-title class="patients-header__title py-4 px-4">
        <div class="d-flex align-center flex-wrap" style="width: 100%; gap: 12px;">
          <div class="header-brand">
            <div class="header-icon">
              <v-icon color="primary">mdi-account-group-outline</v-icon>
            </div>
            <div>
              <div class="text-h6 font-weight-bold mb-0">Patients</div>
              <div class="caption grey--text text--darken-1">
                Search, view and manage registered patients
              </div>
            </div>
          </div>

          <v-spacer></v-spacer>

          <v-btn
              v-if="hasFullAccess()"
              color="primary"
              small
              depressed
              class="header-action-btn"
              @click="phoneNumberChangeList"
          >
            <v-icon left small>mdi-history</v-icon>
            Patient List Log
          </v-btn>
        </div>
      </v-card-title>
    </v-card>

    <!-- Search Card (vision-wise) -->
    <v-card class="mb-4 search-card" elevation="1">
      <v-card-title class="py-3 px-4">
        <v-icon left color="primary" small>mdi-magnify</v-icon>
        <span class="subtitle-1 font-weight-bold">Search Patients</span>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="px-4 py-3">
        <div class="search-bar">
          <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              single-line
              hide-details
              outlined
              dense
              clearable
              label="Search patient by ID, name, phone, referral..."
              placeholder="Start typing (min 5 characters for server search)"
              class="search-input"
          ></v-text-field>
        </div>
      </v-card-text>
    </v-card>

    <!-- KPI -->
    <v-row class="mb-4" dense>
      <v-col cols="12" sm="4">
        <v-card class="kpi-card kpi-card--primary" elevation="1">
          <v-card-text class="d-flex align-center py-3">
            <div class="kpi-icon kpi-icon--primary">
              <v-icon color="primary">mdi-account-multiple-outline</v-icon>
            </div>
            <div>
              <div class="caption grey--text text--darken-1 text-uppercase font-weight-medium">Total Patients</div>
              <div class="text-h5 font-weight-bold">{{ desserts.length || 0 }}</div>
              <div class="caption grey--text">Across current list</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="kpi-card kpi-card--info" elevation="1">
          <v-card-text class="d-flex align-center py-3">
            <div class="kpi-icon kpi-icon--info">
              <v-icon color="info">mdi-filter-outline</v-icon>
            </div>
            <div>
              <div class="caption grey--text text--darken-1 text-uppercase font-weight-medium">Filtered Results</div>
              <div class="text-h5 font-weight-bold">{{ search ? filteredCount : (desserts.length || 0) }}</div>
              <div class="caption grey--text">{{ search ? 'Matching current search' : 'No filter applied' }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="kpi-card kpi-card--success" elevation="1">
          <v-card-text class="d-flex align-center py-3">
            <div class="kpi-icon kpi-icon--success">
              <v-icon color="success">mdi-shield-check-outline</v-icon>
            </div>
            <div>
              <div class="caption grey--text text--darken-1 text-uppercase font-weight-medium">Verified DOB</div>
              <div class="text-h5 font-weight-bold success--text">{{ verifiedDobCount }}</div>
              <div class="caption grey--text">Date of birth confirmed</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card elevation="1" class="patients-table-card">
      <v-card-title class="py-3 px-4 table-card-title">
        <v-icon left color="primary" small>mdi-account-multiple-outline</v-icon>
        <span class="subtitle-1 font-weight-bold">Patient List</span>
        <span class="record-count">{{ desserts.length }} records</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="initialize" :loading="loadingstatus">
          <v-icon small>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-data-table
          :headers="headers"
          :items="desserts"
          :search="search"
          :loading="loadingstatus"
          loading-text="Loading patients..."
          class="patients-table"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
      >
        <template v-slot:item.patient_id="{ item }">
          <span class="patient-id-pill">{{ item.patient_id }}</span>
        </template>

        <template v-slot:item.patient_name="{ item }">
          <div class="patient-name-cell">
            <div class="avatar-initial" :class="genderClass(item.patient_sex)">
              {{ initials(item.patient_name) }}
            </div>
            <div class="patient-name-text">{{ item.patient_name || '—' }}</div>
          </div>
        </template>

        <template v-slot:item.patient_phone="{ item }">
          <span class="mono-text">
            <v-icon x-small>mdi-phone-outline</v-icon>
            {{ item.patient_phone || '—' }}
          </span>
        </template>

        <template v-slot:item.patient_dob="{ item }">
          <div class="dob-cell">
            <span class="dob-value">{{ item.patient_dob || '—' }}</span>
            <v-chip
                v-if="item.dob_not_known_check == 1"
                x-small
                outlined
                color="warning"
                class="dob-warning-pill"
            >
              <v-icon left x-small>mdi-alert-circle-outline</v-icon>
              Not Verified
            </v-chip>
          </div>
        </template>

        <template v-slot:item.patient_sex="{ item }">
          <v-chip
              x-small
              outlined
              class="gender-chip"
              :color="item.patient_sex === 'Male' ? 'info' : item.patient_sex === 'Female' ? 'pink' : 'grey'"
          >
            <v-icon left x-small>{{ genderIcon(item.patient_sex) }}</v-icon>
            {{ item.patient_sex || '—' }}
          </v-chip>
        </template>

        <template v-slot:item.patient_occupation="{ item }">
          <span class="cell-text">{{ item.patient_occupation || '—' }}</span>
        </template>

        <template v-slot:item.corporate_client.ps_name="{ item }">
          <span class="cell-text">{{ (item.corporate_client && item.corporate_client.ps_name) || '—' }}</span>
        </template>

        <template v-slot:item.user.full_name="{ item }">
          <span class="cell-text">{{ (item.user && item.user.full_name) || '—' }}</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="row-actions">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    color="primary"
                    class="action-btn"
                    @click="get_appointment(item)"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-calendar-plus</v-icon>
                </v-btn>
              </template>
              <span>Set Appointment</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    color="info"
                    class="action-btn"
                    @click="get_ot_schedule(item)"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-calendar-clock</v-icon>
                </v-btn>
              </template>
              <span>Set OT Schedule</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    color="warning"
                    class="action-btn"
                    @click="create_invoice_investigation(item)"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-test-tube</v-icon>
                </v-btn>
              </template>
              <span>Create Investigation Invoice</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    color="success"
                    class="action-btn"
                    @click="viewItem(item)"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
              <span>View Patient Details</span>
            </v-tooltip>

            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                    icon
                    x-small
                    color="secondary"
                    class="action-btn"
                    @click="editItem(item)"
                    v-bind="attrs"
                    v-on="on"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit Patient Data</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon large color="grey lighten-1">mdi-account-search-outline</v-icon>
            <div class="subtitle-1 grey--text mt-2">No patients found</div>
            <div class="caption grey--text mb-3">Try a different search or reset the list</div>
            <v-btn color="primary" depressed small @click="initialize">
              <v-icon left small>mdi-refresh</v-icon>
              Reset
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<style scoped>
.patients-page {
  padding-bottom: 24px;
}

.patients-header {
  border-radius: 10px !important;
  overflow: hidden;
}

.header-brand {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(25, 118, 210, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-action-btn {
  height: 32px !important;
  font-size: 12px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-weight: 600 !important;
}

.search-card {
  border-radius: 10px !important;
  overflow: hidden;
}

.search-bar {
  padding: 4px 2px;
}

.search-input {
  max-width: 100%;
}

.kpi-card {
  border-radius: 10px !important;
  height: 100%;
}

.kpi-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.kpi-icon--primary { background: rgba(25, 118, 210, 0.1); }
.kpi-icon--info { background: rgba(3, 169, 244, 0.1); }
.kpi-icon--success { background: rgba(76, 175, 80, 0.1); }

.patients-table-card {
  border-radius: 10px !important;
  overflow: hidden;
}

.table-card-title {
  min-height: 56px;
}

.record-count {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 2px 8px;
  margin-left: 8px;
  letter-spacing: 0.4px;
}

/* ===== Patients table spacing / alignment ===== */
.patients-table ::v-deep th {
  white-space: nowrap !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  color: #475569 !important;
  letter-spacing: 0.2px;
  padding: 14px 16px !important;
  height: 48px !important;
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  vertical-align: middle !important;
}

.patients-table ::v-deep td {
  padding: 14px 16px !important;
  height: auto !important;
  min-height: 64px;
  vertical-align: middle !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 13px;
  color: #334155;
}

.patients-table ::v-deep tbody tr:hover {
  background: #f8fafc !important;
}

.patients-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.patient-id-pill {
  display: inline-block;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  color: #1565c0;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  padding: 4px 10px;
  border-radius: 999px;
  white-space: nowrap;
  line-height: 1.3;
}

.patient-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 160px;
  padding: 2px 0;
}

.avatar-initial {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.avatar-male { background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%); }
.avatar-female { background: linear-gradient(135deg, #f472b6 0%, #be185d 100%); }
.avatar-neutral { background: linear-gradient(135deg, #94a3b8 0%, #475569 100%); }

.patient-name-text {
  font-weight: 600;
  font-size: 13px;
  line-height: 1.35;
  color: #0f172a;
  white-space: nowrap;
}

.mono-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #475569;
  white-space: nowrap;
}

.mono-text .v-icon {
  color: #94a3b8 !important;
}

.dob-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 110px;
}

.dob-value {
  font-size: 12px;
  color: #334155;
  white-space: nowrap;
}

.dob-warning-pill {
  align-self: flex-start;
  height: 22px !important;
}

.gender-chip {
  height: 24px !important;
}

.cell-text {
  display: inline-block;
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  flex-wrap: nowrap;
  min-width: 160px;
}

.action-btn {
  margin: 0 !important;
  transition: transform 0.15s ease !important;
}

.action-btn:hover {
  transform: translateY(-1px);
}
</style>

<script>
import atshq from "../../../js/setup";
import PermissionName from "@/js/PermissionName";

export default {
  data() {
    return {
      dialog: false,
      notifications: false,
      sound: true,
      widgets: false,
      returnUrl: atshq.defaults.headers.common.app_URL,
      search: "",
      loadingstatus: true,
      headers: [
        { text: "ID", align: "start", sortable: true, value: "patient_id", width: "120px" },
        { text: "Name", value: "patient_name", width: "200px" },
        { text: "Phone", value: "patient_phone", width: "140px" },
        { text: "DOB", value: "patient_dob", width: "130px" },
        { text: "Gender", value: "patient_sex", width: "110px" },
        { text: "Occupation", value: "patient_occupation", width: "140px" },
        { text: "Referral", value: "corporate_client.ps_name", width: "160px" },
        { text: "Created By", value: "user.full_name", width: "140px" },
        { text: "Action", value: "actions", sortable: false, width: "170px", align: "start" },
      ],
      desserts: [],
      items: [
        {
          text: "Dashboard",
          disabled: false,
          href: "/",
        },
        {
          text: "Patients",
          disabled: true,
          href: "patients",
        },
      ],
    };
  },
  computed: {
    verifiedDobCount() {
      return (this.desserts || []).filter(
        (d) => d && d.dob_not_known_check != 1 && d.patient_dob
      ).length;
    },
    filteredCount() {
      if (!this.search) return this.desserts.length;
      const q = String(this.search).toLowerCase();
      return (this.desserts || []).filter((d) => {
        return [
          d.patient_id,
          d.patient_name,
          d.patient_phone,
          d.patient_dob,
          d.patient_sex,
          d.patient_occupation,
          d.patient_blood_group,
          d.admission_date,
          d.corporate_client && d.corporate_client.ps_name,
          d.user && d.user.full_name,
        ].some((v) => String(v == null ? "" : v).toLowerCase().includes(q));
      }).length;
    },
  },
  created() {
    atshq.defaults.headers.common["Authorization"] =
        "Bearer " + localStorage.getItem("token");
    this.initialize();
  },
  watch: {
    search() {
      if (this.search) {
        this.patientListSearch();
      } else {
        this.initialize();
      }
    },
  },
  mounted() {
    this.permissionExists(PermissionName.PATIENTS);
    if (this.search) {
      this.patientListSearch();
    }
  },
  methods: {
    initialize() {
      this.loadingstatus = true;
      atshq
          .get("patients/patients")
          .then((response) => {
            this.loadingstatus = false;
            this.desserts = response.data.data;
          })
          .catch((e) => {
            this.loadingstatus = false;
            this.$noty.error("Oops, something went wrong!");
            this.$noty.error(" " + e);
          });
    },
    patientListSearch() {
      // Vision-wise: server search only after min 5 characters
      if (!this.search || String(this.search).length < 5) {
        return;
      }
      this.loadingstatus = true;
      atshq
          .get("patients/patient-list-search?search_data=" + encodeURIComponent(this.search))
          .then((response) => {
            this.desserts = response.data.patients || response.data.data || [];
            this.loadingstatus = false;
          })
          .catch((e) => {
            this.loadingstatus = false;
            this.$noty.error("Oops, something went wrong!");
            this.$noty.error(" " + e);
          });
    },
    clearSearch() {
      this.search = "";
      this.initialize();
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/).slice(0, 2);
      return parts.map((p) => p.charAt(0).toUpperCase()).join("");
    },
    genderClass(sex) {
      const s = String(sex || "").toLowerCase();
      if (s === "male") return "avatar-male";
      if (s === "female") return "avatar-female";
      return "avatar-neutral";
    },
    genderIcon(sex) {
      const s = String(sex || "").toLowerCase();
      if (s === "male") return "mdi-gender-male";
      if (s === "female") return "mdi-gender-female";
      return "mdi-account-outline";
    },
    editItem(item) {
      window.location.href = "patient-create/" + item.patient_id;
    },
    viewItem(item) {
      window.location.href = "patient-details/" + item.patient_id;
    },
    get_appointment(item) {
      window.location.href = "/appointment/create/" + item.patient_id;
    },
    get_ot_schedule(item) {
      window.location.href = "ot-module/schedule-create?patient_id=" + item.patient_id;
    },
    create_invoice(item) {
      window.open(
          this.returnUrl + "front_desk/invoice?patient_id=" + item.patient_id
      );
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    save() {
      if (this.editedIndex > -1) {
        Object.assign(this.desserts[this.editedIndex], this.editedItem);
      } else {
        this.desserts.push(this.editedItem);
      }
      this.close();
    },
    invoice_gen(item) {
      this.$router.push("/invoice-create/" + item.patient_id);
    },
    create_invoice_investigation(item) {
      window.open("/invoice/invoice-create-investigation/" + item.patient_id);
    },
    phoneNumberChangeList() {
      window.location.href = "/patient-list-logs";
    },
  },
};
</script>
